import { ApiError } from './errors';

/**
 * Read-only preview transport.
 *
 * Replaces the network layer with a recording of what the real server returned,
 * so the whole interface can be opened from a single file with nothing installed.
 *
 * Two things it deliberately is not:
 *
 *  1. It is not a mock-up. Every screen, every chart, every warning below is the
 *     real React application rendering real responses captured from the real
 *     engine. If the platform gets a number wrong, the preview gets it wrong in
 *     exactly the same way — which is the only thing that makes a preview worth
 *     looking at.
 *
 *  2. It is not a sandbox you can change things in. Saving an entry point or
 *     editing a report is refused with an explanation rather than pretended.
 *     A preview that appears to accept an edit and silently discards it teaches
 *     people the wrong thing about the product.
 *
 * The whole module is compiled out of production builds — `DEMO` is a build-time
 * constant, so the bundle the platform actually ships contains none of this.
 */

export const DEMO = import.meta.env.VITE_DEMO === '1';

interface Fixture {
  GET: Record<string, unknown>;
  POST: Record<string, unknown>;
  RUNS: Record<string, any>;
  EXPORTS: Record<string, Record<string, { mime: string; name: string; b64: string }>>;
  DEFAULT_EXPORT: Record<string, string>;
}

function fixture(): Fixture {
  const f = (window as any).__DEMO_FIXTURE__;
  return f ?? { GET: {}, POST: {}, RUNS: {}, EXPORTS: {}, DEFAULT_EXPORT: {} };
}

/** A short pause so loading states are actually seen rather than flashing past. */
const pause = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Reported in the preview banner when a chosen filter has no recording behind it. */
let substituted: string | null = null;
export function lastSubstitution(): string | null {
  return substituted;
}

function findRun(slug: string, params: any): any {
  const fx = fixture();
  const preset = params?.dateRange?.preset ?? '';
  const team = params?.team ?? '';
  const exact = fx.RUNS[`${slug}|${preset}|${team}`];
  if (exact) {
    substituted = null;
    return exact;
  }
  // Fall back along the axes that were recorded, most specific first, and SAY
  // which one was used. Silently serving a different period than the one asked
  // for is the one behaviour that would make this preview actively misleading.
  const candidates = Object.keys(fx.RUNS).filter((k) => k.startsWith(`${slug}|`));
  const samePreset = candidates.find((k) => k.split('|')[1] === preset);
  const chosen = samePreset ?? candidates[0];
  if (!chosen) return null;
  const [, gotPreset, gotTeam] = chosen.split('|');
  substituted =
    `Showing the recorded run for ${gotPreset.replace(/_/g, ' ')}${gotTeam ? ` · ${gotTeam}` : ''}. ` +
    'The preview holds a fixed set of runs rather than a live engine, so this combination was not recorded.';
  return fx.RUNS[chosen];
}

function lookupGet(path: string): unknown {
  const fx = fixture();
  if (path in fx.GET) return fx.GET[path];
  const bare = path.split('?')[0];
  if (bare in fx.GET) return fx.GET[bare];
  const prefix = Object.keys(fx.GET).find((k) => k.split('?')[0] === bare);
  return prefix ? fx.GET[prefix] : undefined;
}

const READ_ONLY =
  'This is a read-only preview, so nothing can be saved. Everything you can see is real output from the real engine — ' +
  'but editing, syncing and sending are turned off, because there is no server behind this file.';

export async function demoRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? 'GET').toUpperCase();
  const body = typeof init?.body === 'string' ? JSON.parse(init.body) : undefined;
  await pause(method === 'GET' ? 90 : 260);

  if (method === 'GET') {
    const hit = lookupGet(path);
    if (hit !== undefined) return hit as T;
    // Administration pages are left out of a preview by default: there is no
    // reason for a file that gets emailed around to carry the integration
    // configuration or the list of who has an account. Say that, rather than
    // leaving someone staring at a dead page wondering what broke.
    const isAdminArea = /^\/api\/(connections|requests|users|audit|admin)\b/.test(path);
    throw new ApiError(
      isAdminArea
        ? 'Administration screens are left out of this preview on purpose — it carries reports, not your entry points, ' +
          'credentials or user list. Rebuild with --include-admin if you need them in it.'
        : `Nothing recorded for ${path} in this preview.`,
      404,
    );
  }

  if (method === 'POST') {
    if (path === '/api/auth/login' || path === '/api/auth/me') return fixture().GET['/api/auth/me'] as T;
    if (path === '/api/auth/logout') return undefined as T;

    const runMatch = path.match(/^\/api\/reports\/([^/]+)\/run$/);
    if (runMatch) {
      const run = findRun(runMatch[1], body?.params ?? {});
      if (!run) throw new ApiError('No recorded run for that report in this preview.', 404);
      return run as T;
    }

    const testMatch = path.match(/^\/api\/connections\/(\d+)\/test$/);
    if (testMatch) {
      const hit = fixture().POST[`${path}::{}`];
      if (hit) return hit as T;
      return { ok: false, detail: 'Connection tests need a live server — not available in the preview.' } as T;
    }
  }

  throw new ApiError(READ_ONLY, 405);
}

/**
 * Export links are plain anchors to /api/runs/…/export.pdf, which cannot work
 * from a file. Intercept the click and hand over the real artefact instead —
 * these are the genuine PDF and Excel files the engine produced for the recorded
 * run, not renderings of them.
 */
export function installExportInterceptor(): void {
  document.addEventListener(
    'click',
    (event) => {
      const anchor = (event.target as HTMLElement | null)?.closest?.('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href') ?? '';
      const match = href.match(/^\/api\/runs\/([^/]+)\/(export\.pdf|export\.xlsx|print)$/);
      if (!match) return;
      event.preventDefault();
      const [, runId, kind] = match;
      const fx = fixture();
      if (kind === 'print') {
        window.alert('The print view needs a live server. Use Export PDF — that is the same document.');
        return;
      }
      const fmt = kind === 'export.pdf' ? 'pdf' : 'xlsx';
      const slug = (fx.GET[`/api/runs/${runId}`] as any)?.reportSlug;
      const file = fx.EXPORTS[runId]?.[fmt] ?? fx.EXPORTS[fx.DEFAULT_EXPORT[slug] ?? '']?.[fmt];
      if (!file) {
        window.alert(
          'The preview carries one recorded export per report rather than one per filter combination. ' +
            'Reset the filters to the default and try again.',
        );
        return;
      }
      const bytes = Uint8Array.from(atob(file.b64), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: file.mime }));
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    },
    true,
  );
}
